import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBook, FaSearch, FaFilter, FaArrowRight, FaCalendarAlt } from 'react-icons/fa';
import SEO from '../components/SEO';
import useGuides from '../hooks/useGuides';
import { formatDate } from '../utils/stringUtils';
import { Helmet } from 'react-helmet-async';

const GuidesList = () => {
  const { guides, categories, loading, error } = useGuides();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredGuides, setFilteredGuides] = useState([]);
  
  useEffect(() => {
    if (guides && guides.length > 0) {
      let filtered = [...guides];
      
      // Filter by category
      if (activeCategory !== 'all') {
        filtered = filtered.filter(guide => guide.category === activeCategory);
      }
      
      // Filter by search term
      if (searchTerm.trim() !== '') {
        filtered = filtered.filter(guide => 
          guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guide.summary.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setFilteredGuides(filtered);
    } else {
      setFilteredGuides([]);
    }
  }, [guides, activeCategory, searchTerm]);

  return (
    <>
      <SEO 
        title="Hướng dẫn tại PSA Roleplay"
        description="Tất cả các hướng dẫn để giúp bạn chơi PSA Roleplay hiệu quả nhất."
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Hướng dẫn tại PSA Roleplay",
            "description": "Tất cả các hướng dẫn để giúp bạn chơi PSA Roleplay hiệu quả nhất.",
            "url": "https://psa.world/guides"
          })}
        </script>
        <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Trang chủ",
                "item": "https://psa.world"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Hướng dẫn tại PSA Roleplay",
                "item": "https://psa.world/guides"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen pt-24 pb-16 bg-dark-purple-main relative overflow-hidden">
        <div className="page-guides-bg absolute inset-0 opacity-30"></div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-purple to-dark-purple-main"></div>
        
        <motion.div 
          className="container mx-auto px-4 relative z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col items-center justify-center text-center mb-10">
            <motion.h1 
              className="text-5xl md:text-6xl font-bold mb-4" 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-white neon-glow-white">HƯỚNG DẪN</span>
            </motion.h1>
            
            <motion.div 
              className="w-24 h-1 bg-accent-teal mb-6"
              initial={{ width: 0 }}
              animate={{ width: 96 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            />
            
            <motion.p 
              className="text-light/80 text-lg max-w-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Tìm hiểu các kỹ năng mới và cải thiện trải nghiệm chơi game của bạn với hướng dẫn chi tiết từ cộng đồng PSA.
            </motion.p>
          </div>

          <motion.div 
            className="glass-effect p-6 rounded-xl mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-grow relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-light/40">
                  <FaSearch />
                </div>
                <input
                  type="text"
                  placeholder="Tìm kiếm hướng dẫn..."
                  className="w-full py-3 pl-10 pr-4 bg-dark/40 text-white border border-white/10 focus:border-accent-teal/50 outline-none rounded-lg transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="relative">
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-thin">
                  <button
                    className={`whitespace-nowrap px-4 py-2 rounded-lg transition-all ${
                      activeCategory === 'all'
                        ? 'bg-accent-teal text-dark font-medium'
                        : 'bg-dark/40 text-light/80 hover:bg-dark/60'
                    }`}
                    onClick={() => setActiveCategory('all')}
                  >
                    <FaFilter className="inline mr-2" />
                    Tất cả
                  </button>
                  
                  {categories.map(category => (
                    <button
                      key={category}
                      className={`whitespace-nowrap px-4 py-2 rounded-lg transition-all ${
                        activeCategory === category
                          ? 'bg-accent-teal text-dark font-medium'
                          : 'bg-dark/40 text-light/80 hover:bg-dark/60'
                      }`}
                      onClick={() => setActiveCategory(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              [...Array(6)].map((_, index) => (
                <motion.div 
                  key={index}
                  className="glassmorphism-card animate-pulse"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  style={{ height: '300px' }}
                >
                  <div className="h-40 bg-white/10 rounded-t-xl"></div>
                  <div className="p-6">
                    <div className="h-6 bg-white/10 rounded mb-3 w-3/4"></div>
                    <div className="h-4 bg-white/10 rounded mb-2 w-full"></div>
                    <div className="h-4 bg-white/10 rounded w-2/3"></div>
                  </div>
                </motion.div>
              ))
            ) : error ? (
              <div className="col-span-3 glassmorphism-card p-8 text-center">
                <p className="text-xl text-light/80">Có lỗi khi tải dữ liệu. Vui lòng thử lại sau.</p>
              </div>
            ) : filteredGuides.length === 0 ? (
              <div className="col-span-3 glassmorphism-card p-8 text-center">
                <p className="text-xl text-light/80">Không tìm thấy hướng dẫn nào phù hợp với tìm kiếm.</p>
              </div>
            ) : (
              filteredGuides.map((guide, index) => (
                <motion.div 
                  key={guide.id} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * (index % 6), duration: 0.5 }}
                >
                  <Link to={`/guides/${guide.slug}`} className="block h-full">
                    <div className="glassmorphism-card h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                      <div 
                        className="h-48 bg-cover bg-center rounded-t-xl relative"
                        style={{ backgroundImage: `url(${guide.image})` }}
                      >
                        <div className="absolute top-4 left-4 bg-accent-teal/80 text-dark text-sm px-3 py-1 rounded-full font-medium">
                          {guide.category}
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-center text-light/60 text-sm mb-2">
                          <FaCalendarAlt className="mr-2" />
                          <span>{formatDate(guide.date)}</span>
                        </div>
                        
                        <h2 className="text-xl font-bold mb-3 text-white line-clamp-2">
                          {guide.title}
                        </h2>
                        
                        <p className="text-light/80 mb-4 line-clamp-2">
                          {guide.summary}
                        </p>
                        
                        <div className="inline-flex items-center text-accent-teal hover:text-accent-teal/80 transition-colors">
                          Xem chi tiết <FaArrowRight className="ml-2" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>
          
          {guides.length > 0 && filteredGuides.length > 0 && (
            <motion.div 
              className="flex justify-center mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <Link 
                to="/guides/huong-dan-co-ban-cho-nguoi-moi-bat-dau-tai-psa-roleplay"
                className="btn btn-primary py-3 px-8 flex items-center gap-2"
              >
                <FaBook className="text-lg" />
                <span>Bắt đầu với hướng dẫn cho người mới</span>
              </Link>
            </motion.div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default GuidesList;
